// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Alesson Lucas Oliveira de Queiroz. Terms: LICENSE and NOTICE.md.
// The editor's words, in Portuguese, Spanish and English.

export type Lang = "pt" | "es" | "en";

export const LANGS: Lang[] = ["pt", "es", "en"];

const words = {
  // Navigation.
  navPlay: { pt: "Tocar", es: "Tocar", en: "Play" },
  navLearn: { pt: "Aprender", es: "Aprender", en: "Learn" },
  navManual: { pt: "Manual", es: "Manual", en: "Manual" },
  navInside: { pt: "Por dentro", es: "Por dentro", en: "Inside" },
  tagline: {
    pt: "linguagem orientada a performance musical e composição algorítmica",
    es: "lenguaje orientado a la performance musical y la composición algorítmica",
    en: "a language for musical performance and algorithmic composition",
  },
  light: { pt: "Claro", es: "Claro", en: "Light" },
  dark: { pt: "Escuro", es: "Oscuro", en: "Dark" },
  theme: { pt: "Aparência", es: "Apariencia", en: "Appearance" },
  rhythmMap: { pt: "Mapa do ritmo", es: "Mapa del ritmo", en: "Rhythm map" },
  rhythmHelp: {
    pt: "A música no tempo: cada coluna é um compasso, na ordem em que toca, com a seção em cima, e cada linha é uma parte. A linha laranja mostra onde a música está, e o mapa rola junto. Arraste uma batida para movê-la, clique num espaço vazio para acrescentar, clique numa batida para tirar: o código muda junto.",
    es: "La música en el tiempo: cada columna es un compás, en el orden en que suena, con la sección arriba, y cada fila es una parte. La línea naranja muestra dónde va la música, y el mapa se desplaza con ella. Arrastra un golpe para moverlo, haz clic en un espacio vacío para agregar, haz clic en un golpe para quitarlo: el código cambia con él.",
    en: "The piece in time: each column is a bar, in the order it plays, with its section on top, and each row is a part. The orange line shows where the music is, and the map scrolls along. Drag a hit to move it, click an empty place to add one, click a hit to take it away: the code changes with it.",
  },
  grid: { pt: "Grade", es: "Cuadrícula", en: "Grid" },
  gridAuto: { pt: "auto", es: "auto", en: "auto" },
  eighths: { pt: "colcheias", es: "corcheas", en: "eighths" },
  sixteenths: { pt: "semicolcheias", es: "semicorcheas", en: "sixteenths" },
  triplets: { pt: "tercinas", es: "tresillos", en: "triplets" },

  // The playground.
  play: { pt: "Tocar", es: "Tocar", en: "Play" },
  stop: { pt: "Parar", es: "Detener", en: "Stop" },
  stage: { pt: "Palco", es: "Escenario", en: "Stage" },
  stageExit: { pt: "Sair", es: "Salir", en: "Exit" },
  stageSplit: { pt: "2 colunas", es: "2 columnas", en: "2 columns" },
  stageFit: { pt: "Caber na tela", es: "Caber en pantalla", en: "Fit the screen" },
  follow: { pt: "Seguir", es: "Seguir", en: "Follow" },
  // The account (a MusiMind account) and its pieces.
  acctSignIn: { pt: "Entrar", es: "Entrar", en: "Sign in" },
  acctSignInHint: {
    pt: "Entrar com a sua conta MusiMind e guardar as suas peças",
    es: "Entrar con tu cuenta MusiMind y guardar tus piezas",
    en: "Sign in with your MusiMind account and keep your pieces",
  },
  acctSignUp: { pt: "Criar conta", es: "Crear cuenta", en: "Sign up" },
  acctTitle: { pt: "Sua conta", es: "Tu cuenta", en: "Your account" },
  acctAbout: {
    pt: "A conta do Aratu é a do MusiMind: o mesmo e-mail e a mesma senha abrem os dois, e as peças guardadas aqui aparecem lá.",
    es: "La cuenta de Aratu es la de MusiMind: el mismo correo y la misma contraseña abren los dos, y las piezas guardadas aquí aparecen allí.",
    en: "Your Aratu account is your MusiMind account: the same e-mail and password open both, and the pieces kept here show up there.",
  },
  acctEmail: { pt: "E-mail", es: "Correo", en: "E-mail" },
  acctPassword: { pt: "Senha", es: "Contraseña", en: "Password" },
  acctNewPassword: { pt: "Senha (8 caracteres ou mais)", es: "Contraseña (8 caracteres o más)", en: "Password (8 characters or more)" },
  acctForgot: { pt: "Esqueci a senha", es: "Olvidé la contraseña", en: "Forgot the password" },
  acctName: { pt: "Nome", es: "Nombre", en: "Name" },
  acctBirth: { pt: "Data de nascimento", es: "Fecha de nacimiento", en: "Date of birth" },
  acctGuardian: {
    pt: "Menor de 18: dados de um responsável",
    es: "Menor de 18: datos de un responsable",
    en: "Under 18: a parent or guardian",
  },
  acctGuardianName: { pt: "Nome do responsável", es: "Nombre del responsable", en: "Their name" },
  acctGuardianEmail: { pt: "E-mail do responsável", es: "Correo del responsable", en: "Their e-mail" },
  acctTerms: {
    pt: "Li e aceito os {terms} e a {privacy} do MusiMind.",
    es: "Leí y acepto los {terms} y la {privacy} de MusiMind.",
    en: "I have read and accept MusiMind's {terms} and {privacy}.",
  },
  acctTermsLink: { pt: "Termos de uso", es: "Términos de uso", en: "Terms of use" },
  acctPrivacyLink: { pt: "Política de privacidade", es: "Política de privacidad", en: "Privacy policy" },
  acctCreate: { pt: "Criar a conta", es: "Crear la cuenta", en: "Create the account" },
  acctSignOut: { pt: "Sair", es: "Salir", en: "Sign out" },
  acctThisPiece: { pt: "Esta peça", es: "Esta pieza", en: "This piece" },
  acctNotInAccount: {
    pt: "A peça aberta está só neste navegador.",
    es: "La pieza abierta está solo en este navegador.",
    en: "The piece open here is only in this browser.",
  },
  acctSaveToAccount: { pt: "Guardar na minha conta", es: "Guardar en mi cuenta", en: "Keep it in my account" },
  acctTitleLabel: { pt: "Nome da peça", es: "Nombre de la pieza", en: "The piece's name" },
  acctSaveVersion: { pt: "Salvar versão", es: "Guardar versión", en: "Save a version" },
  acctVersions: { pt: "Versões", es: "Versiones", en: "Versions" },
  acctVersion: { pt: "Versão", es: "Versión", en: "Version" },
  acctNoVersions: { pt: "Nenhuma versão salva ainda.", es: "Ninguna versión guardada todavía.", en: "No version saved yet." },
  acctRestore: { pt: "Voltar a esta versão", es: "Volver a esta versión", en: "Go back to this version" },
  acctRestoreAsk: {
    pt: "Voltar a peça para a versão {n}? O que está no editor agora vira uma versão também.",
    es: "¿Volver la pieza a la versión {n}? Lo que está ahora en el editor se guarda también como versión.",
    en: "Take the piece back to version {n}? What is in the editor now is kept as a version too.",
  },
  acctNew: { pt: "Nova peça", es: "Pieza nueva", en: "New piece" },
  acctMine: { pt: "Minhas peças", es: "Mis piezas", en: "My pieces" },
  acctLoading: { pt: "Carregando…", es: "Cargando…", en: "Loading…" },
  acctNone: {
    pt: "Nenhuma peça guardada ainda.",
    es: "Ninguna pieza guardada todavía.",
    en: "No piece kept yet.",
  },
  acctDelete: { pt: "Apagar", es: "Borrar", en: "Delete" },
  acctDeleteAsk: { pt: "Apagar a peça “{title}”?", es: "¿Borrar la pieza “{title}”?", en: "Delete the piece “{title}”?" },
  acctReplaceAsk: {
    pt: "A peça aberta não está na sua conta. Abrir outra no lugar dela? (Ctrl+Z traz esta de volta.)",
    es: "La pieza abierta no está en tu cuenta. ¿Abrir otra en su lugar? (Ctrl+Z trae esta de vuelta.)",
    en: "The piece open here is not in your account. Open another in its place? (Ctrl+Z brings this one back.)",
  },
  acctUntitled: { pt: "Peça sem nome", es: "Pieza sin nombre", en: "Untitled piece" },
  acctUnsaved: { pt: "Alterações ainda não salvas…", es: "Cambios aún sin guardar…", en: "Changes not saved yet…" },
  acctSaving: { pt: "Salvando…", es: "Guardando…", en: "Saving…" },
  acctSaved: { pt: "Salvo na sua conta.", es: "Guardado en tu cuenta.", en: "Saved in your account." },
  acctVersionSaved: { pt: "Versão {n} salva.", es: "Versión {n} guardada.", en: "Version {n} saved." },
  acctNotSaved: { pt: "Não salvou: {why}", es: "No se guardó: {why}", en: "Not saved: {why}" },
  acctOpened: { pt: "Peça aberta da sua conta.", es: "Pieza abierta de tu cuenta.", en: "Piece opened from your account." },
  acctRestored: { pt: "De volta à versão {n}.", es: "De vuelta a la versión {n}.", en: "Back to version {n}." },
  acctOffline: {
    pt: "sem conexão com o MusiMind; tento de novo na próxima mudança",
    es: "sin conexión con MusiMind; lo intento de nuevo en el próximo cambio",
    en: "no connection to MusiMind; trying again at the next change",
  },
  acctExpired: { pt: "a sessão acabou; entre de novo", es: "la sesión terminó; entra de nuevo", en: "the session ended; sign in again" },
  acctLimit: {
    pt: "a conta chegou ao limite de peças; apague alguma",
    es: "la cuenta llegó al límite de piezas; borra alguna",
    en: "the account has reached its number of pieces; delete one",
  },
  acctTooLarge: {
    pt: "a peça passou de 64 KB",
    es: "la pieza pasó de 64 KB",
    en: "the piece is over 64 KB",
  },
  acctRefused: {
    pt: "o nome ou os comentários da peça foram barrados pela moderação",
    es: "el nombre o los comentarios de la pieza fueron bloqueados por la moderación",
    en: "the piece's name or comments were stopped by moderation",
  },

  // Playing in a DAW, through the Aratu Bridge plugin.
  daw: { pt: "DAW", es: "DAW", en: "DAW" },
  dawHint: {
    pt: "Tocar numa DAW, pelo plugin Aratu Bridge",
    es: "Tocar en una DAW, con el plugin Aratu Bridge",
    en: "Play in a DAW, through the Aratu Bridge plugin",
  },
  dawTitle: { pt: "Tocar na DAW", es: "Tocar en la DAW", en: "Play in the DAW" },
  dawAbout: {
    pt: "Ponha o plugin Aratu Bridge numa trilha da DAW e conecte. O som sai da trilha; o play da DAW começa a música no próximo compasso, o stop para, e o andamento da DAW passa a ser o da peça.",
    es: "Pon el plugin Aratu Bridge en una pista de la DAW y conecta. El sonido sale de la pista; el play de la DAW empieza la música en el compás siguiente, el stop la para, y el tempo de la DAW pasa a ser el de la pieza.",
    en: "Put the Aratu Bridge plugin on a DAW track and connect. The sound comes out of the track; the DAW's play starts the music on its next bar line, its stop stops it, and the DAW's tempo becomes the piece's.",
  },
  dawPort: { pt: "Porta do plugin", es: "Puerto del plugin", en: "Plugin port" },
  dawConnect: { pt: "Conectar", es: "Conectar", en: "Connect" },
  dawDisconnect: { pt: "Desconectar", es: "Desconectar", en: "Disconnect" },
  dawIdle: { pt: "Tocando no navegador.", es: "Sonando en el navegador.", en: "Playing in the browser." },
  dawConnecting: { pt: "Conectando ao plugin…", es: "Conectando con el plugin…", en: "Connecting to the plugin…" },
  dawOpen: {
    pt: "Conectado: o som sai da DAW.",
    es: "Conectado: el sonido sale de la DAW.",
    en: "Connected: the sound comes out of the DAW.",
  },
  dawFailed: {
    pt: "Não conectou. O plugin está aberto, nessa porta?",
    es: "No se conectó. ¿Está abierto el plugin, en ese puerto?",
    en: "Could not connect. Is the plugin open, on that port?",
  },
  dawLost: {
    pt: "A DAW desconectou; o som voltou ao navegador.",
    es: "La DAW se desconectó; el sonido volvió al navegador.",
    en: "The DAW went away; the sound is back in the browser.",
  },
  dawBadPort: {
    pt: "A porta é um número de 1024 a 65535.",
    es: "El puerto es un número de 1024 a 65535.",
    en: "The port is a number from 1024 to 65535.",
  },
  // The legal notices in the footer (the AGPL's Appropriate Legal Notices).
  legal: {
    pt: "Software livre (AGPL-3.0), sem garantia; textos CC BY-SA 4.0",
    es: "Software libre (AGPL-3.0), sin garantía; textos CC BY-SA 4.0",
    en: "Free software (AGPL-3.0), no warranty; texts CC BY-SA 4.0",
  },
  commercialLicense: { pt: "Licença comercial", es: "Licencia comercial", en: "Commercial licence" },
  eqTitle: { pt: "Equalizador", es: "Ecualizador", en: "Equalizer" },
  eqOpen: {
    pt: "Clique para abrir o equalizador e arrastar graves, médios e agudos",
    es: "Haz clic para abrir el ecualizador y arrastrar graves, medios y agudos",
    en: "Click to open the equalizer and drag the lows, mids and highs",
  },
  eqLow: { pt: "Graves", es: "Graves", en: "Lows" },
  eqMid: { pt: "Médios", es: "Medios", en: "Mids" },
  eqHigh: { pt: "Agudos", es: "Agudos", en: "Highs" },
  eqHint: {
    pt: "Arraste um ponto: para os lados muda a frequência, para cima e para baixo o volume. A roda do mouse nos médios estreita ou alarga a faixa.",
    es: "Arrastra un punto: hacia los lados cambia la frecuencia, hacia arriba y abajo el volumen. La rueda del ratón en los medios estrecha o ensancha la banda.",
    en: "Drag a point: sideways changes the frequency, up and down the level. The mouse wheel on the mids narrows or widens the band.",
  },
  followHint: {
    pt: "Seguir a música: a tela vai até o trecho que está tocando",
    es: "Seguir la música: la pantalla va al trozo que está sonando",
    en: "Follow the music: the screen goes to the code that is playing",
  },
  scopeHint: {
    pt: "A onda do som; clique para ver as frequências",
    es: "La onda del sonido; haz clic para ver las frecuencias",
    en: "The sound's wave; click to see its frequencies",
  },
  fontSmaller: { pt: "Letra menor", es: "Letra más pequeña", en: "Smaller letters" },
  fontBigger: { pt: "Letra maior", es: "Letra más grande", en: "Bigger letters" },
  share: { pt: "Compartilhar", es: "Compartir", en: "Share" },
  shareCopied: {
    pt: "Link copiado: cole onde quiser. Quem abrir ouve esta música.",
    es: "Enlace copiado: pégalo donde quieras. Quien lo abra oye esta música.",
    en: "Link copied: paste it anywhere. Whoever opens it hears this piece.",
  },
  shareShown: {
    pt: "Copie este link para compartilhar a música:",
    es: "Copia este enlace para compartir la música:",
    en: "Copy this link to share the piece:",
  },
  shareLong: {
    pt: "O link ficou longo; alguns aplicativos podem cortá-lo.",
    es: "El enlace quedó largo; algunas aplicaciones pueden cortarlo.",
    en: "The link is long; some apps may cut it.",
  },
  sharedOpened: {
    pt: "Música do link aberta. Ctrl+Z volta à que estava aqui.",
    es: "Música del enlace abierta. Ctrl+Z vuelve a la que estaba aquí.",
    en: "The piece from the link is open. Ctrl+Z brings back what was here.",
  },
  sharedBroken: {
    pt: "Não consegui ler a música deste link: talvez ele tenha sido cortado.",
    es: "No pude leer la música de este enlace: quizá se cortó.",
    en: "Could not read the piece in this link: it may have been cut short.",
  },
  record: { pt: "Gravar", es: "Grabar", en: "Record" },
  recordStop: { pt: "Parar gravação", es: "Detener grabación", en: "Stop recording" },
  recordSaved: {
    pt: "Gravação pronta: o arquivo foi baixado.",
    es: "Grabación lista: el archivo se descargó.",
    en: "Recording done: the file was downloaded.",
  },
  scenes: { pt: "Cenas", es: "Escenas", en: "Scenes" },
  sceneEmpty: {
    pt: "Vazia: clique para guardar aqui a música do editor",
    es: "Vacía: haz clic para guardar aquí la música del editor",
    en: "Empty: click to keep the editor's piece here",
  },
  sceneHint: {
    pt: "Clique (ou Ctrl+Shift+{n}) traz esta cena; Shift+clique guarda a música atual aqui",
    es: "Clic (o Ctrl+Shift+{n}) trae esta escena; Shift+clic guarda aquí la música actual",
    en: "Click (or Ctrl+Shift+{n}) brings this scene back; Shift+click keeps the current piece here",
  },
  sceneSaved: { pt: "Cena {n} guardada.", es: "Escena {n} guardada.", en: "Scene {n} kept." },
  sceneRecalled: { pt: "Cena {n} no editor.", es: "Escena {n} en el editor.", en: "Scene {n} is in the editor." },
  sceneComing: {
    pt: "Cena {n}: entra no próximo compasso. Ctrl+Z volta.",
    es: "Escena {n}: entra en el compás siguiente. Ctrl+Z vuelve.",
    en: "Scene {n}: it comes in at the next bar line. Ctrl+Z goes back.",
  },
  recordUnsupported: {
    pt: "Este navegador não consegue gravar o som.",
    es: "Este navegador no puede grabar el sonido.",
    en: "This browser cannot record the sound.",
  },
  examples: { pt: "Exemplos", es: "Ejemplos", en: "Examples" },
  messages: { pt: "Mensagens", es: "Mensajes", en: "Messages" },
  allGood: { pt: "Tudo certo. Pode tocar.", es: "Todo bien. Puedes tocar.", en: "All good. Ready to play." },
  keepsPlaying: {
    pt: "Há erros: continua tocando a última versão que estava certa.",
    es: "Hay errores: sigue sonando la última versión que estaba bien.",
    en: "There are errors: the last version that was right keeps playing.",
  },
  fixFirst: { pt: "Corrija os erros para tocar.", es: "Corrige los errores para tocar.", en: "Fix the errors to play." },
  bar: { pt: "Compasso", es: "Compás", en: "Bar" },
  tempo: { pt: "Andamento", es: "Tempo", en: "Tempo" },
  meter: { pt: "Fórmula", es: "Compás", en: "Meter" },
  line: { pt: "linha", es: "línea", en: "line" },
  error: { pt: "erro", es: "error", en: "error" },
  warning: { pt: "aviso", es: "aviso", en: "warning" },
  next: { pt: "A mudança entra no compasso", es: "El cambio entra en el compás", en: "The change comes in at bar" },
  beat: { pt: "tempo", es: "tiempo", en: "beat" },
  hint: {
    pt: "Ctrl+Enter toca ou atualiza · Ctrl+. para · mude o texto enquanto toca: a mudança entra na próxima barra · Palco (Ctrl+Shift+F): só o código, em tela cheia; Esc sai",
    es: "Ctrl+Enter toca o actualiza · Ctrl+. detiene · cambia el texto mientras suena: el cambio entra en la próxima barra · Escenario (Ctrl+Shift+F): solo el código, en pantalla completa; Esc sale",
    en: "Ctrl+Enter plays or updates · Ctrl+. stops · change the text while it plays: the change comes in at the next bar line · Stage (Ctrl+Shift+F): only the code, full screen; Esc leaves",
  },
  ecosystem: { pt: "Parte do ecossistema", es: "Parte del ecosistema", en: "Part of the ecosystem" },
  source: { pt: "Código-fonte", es: "Código fuente", en: "Source code" },
  loading: { pt: "Carregando…", es: "Cargando…", en: "Loading…" },

  // The Sounds panel.
  sounds: { pt: "Sons", es: "Sonidos", en: "Sounds" },
  soundsHelp: {
    pt: "Cada som tem um código. Escreva-o depois do nome da parte, como em baixo: bass.slap, ou clique no código para colocá-lo onde está o cursor. ▶ toca um compasso dele.",
    es: "Cada sonido tiene un código. Escríbelo después del nombre de la parte, como en bajo: bass.slap, o haz clic en el código para ponerlo donde está el cursor. ▶ toca un compás.",
    en: "Each sound has a code. Write it after the part's name, as in bass: bass.slap, or click the code to put it where the cursor is. ▶ plays a bar of it.",
  },
  soundSearch: { pt: "Buscar um som", es: "Buscar un sonido", en: "Find a sound" },
  soundNone: { pt: "Nenhum som com esse nome.", es: "Ningún sonido con ese nombre.", en: "No sound by that name." },
  soundHear: { pt: "Ouvir", es: "Escuchar", en: "Listen" },
  soundUse: { pt: "Usar este som", es: "Usar este sonido", en: "Use this sound" },
  soundSampled: { pt: "gravado", es: "grabado", en: "recorded" },
  soundLoading: { pt: "baixando…", es: "descargando…", en: "loading…" },
  soundReady: { pt: "pronto", es: "listo", en: "ready" },
  soundsDownloading: { pt: "Baixando sons:", es: "Descargando sonidos:", en: "Downloading sounds:" },
  problemEngine: {
    pt: "O motor de som Manguebyte não carregou ({detail}). Todos os sons estão tocando no motor provisório.",
    es: "El motor de sonido Manguebyte no cargó ({detail}). Todos los sonidos suenan con el motor provisional.",
    en: "Manguebyte's sound engine did not load ({detail}). Every sound is playing on the stand-in engine.",
  },
  problemCopy: {
    pt: "As amostras de {voice} não carregaram no Manguebyte ({detail}). Ele está tocando com a cópia do motor provisório.",
    es: "Las muestras de {voice} no cargaron en Manguebyte ({detail}). Suena con la copia del motor provisional.",
    en: "{voice}'s samples did not load in Manguebyte ({detail}). It is playing from the stand-in engine's copy.",
  },
  problemSynth: {
    pt: "Não foi possível baixar {voice} ({detail}). No lugar dele toca um som provisório parecido, {instead}.",
    es: "No se pudo descargar {voice} ({detail}). En su lugar suena un sonido provisional parecido, {instead}.",
    en: "{voice} could not be downloaded ({detail}). A similar stand-in sound, {instead}, is playing instead.",
  },
  problemStandIn: {
    pt: "Não foi possível preparar {voice} no Manguebyte ({detail}). Ele está tocando no motor provisório.",
    es: "No se pudo preparar {voice} en Manguebyte ({detail}). Suena con el motor provisional.",
    en: "{voice} could not be set up in Manguebyte ({detail}). It is playing on the stand-in engine.",
  },
  detailTimeout: { pt: "sem resposta em {n} s", es: "sin respuesta en {n} s", en: "no answer in {n} s" },
  detailStatus: { pt: "o servidor respondeu {code}", es: "el servidor respondió {code}", en: "the server answered {code}" },
  detailNetwork: { pt: "falha de rede", es: "fallo de red", en: "a network failure" },
  detailWorklet: {
    pt: "este navegador não tem AudioWorklet",
    es: "este navegador no tiene AudioWorklet",
    en: "this browser has no AudioWorklet",
  },
  soundClose: { pt: "Fechar", es: "Cerrar", en: "Close" },
  mbSwitch: {
    pt: "Tocar amostras e baterias pelo motor do Manguebyte",
    es: "Tocar muestras y baterías con el motor de Manguebyte",
    en: "Play samples and drum kits with Manguebyte's engine",
  },
  mbHelp: {
    pt: "As vozes marcadas Manguebyte tocam pelo motor do Manguebyte; as outras, pelo motor provisório do navegador, até o Manguebyte tê-las. Desligado, tudo toca no provisório. A página recarrega ao trocar.",
    es: "Las voces marcadas Manguebyte suenan con el motor de Manguebyte; las demás, con el motor provisional del navegador, hasta que Manguebyte las tenga. Apagado, todo suena con el provisional. La página se recarga al cambiar.",
    en: "Voices marked Manguebyte play through Manguebyte's engine; the others through the browser's stand-in until Manguebyte has them. Off, everything plays in the stand-in. The page reloads when it changes.",
  },
  soundFailed: { pt: "não baixou", es: "no se descargó", en: "did not load" },

  // Twelve-tone rows and melody lists.
  rows: { pt: "Séries", es: "Series", en: "Rows" },
  rowsHelp: {
    pt: "A matriz de cada série da peça. Cada linha é uma forma P, lida da esquerda para a direita, ou R, da direita para a esquerda; cada coluna é uma forma I, de cima para baixo, ou RI, de baixo para cima. Clique num nome para escrevê-lo no código.",
    es: "La matriz de cada serie de la pieza. Cada fila es una forma P, leída de izquierda a derecha, o R, de derecha a izquierda; cada columna es una forma I, de arriba abajo, o RI, de abajo arriba. Haz clic en un nombre para escribirlo en el código.",
    en: "The matrix of each row in the piece. Each line is a P form, read left to right, or R, right to left; each column is an I form, top to bottom, or RI, bottom to top. Click a name to write it into the code.",
  },
  rowsNone: {
    pt: "Esta peça não tem nenhuma série. Escreva, por exemplo: row serie = 0 11 3 4 8 7 9 5 6 1 2 10",
    es: "Esta pieza no tiene ninguna serie. Escribe, por ejemplo: row serie = 0 11 3 4 8 7 9 5 6 1 2 10",
    en: "This piece has no row. Write, for example: row serie = 0 11 3 4 8 7 9 5 6 1 2 10",
  },
  rowsNumbers: { pt: "Ver números", es: "Ver números", en: "Show numbers" },
  rowsNames: { pt: "Ver notas", es: "Ver notas", en: "Show notes" },
  listAll: { pt: "todas", es: "todas", en: "all" },
  listRandom: { pt: "sorteio", es: "azar", en: "random" },
  listHint: {
    pt: "Alt+1 a Alt+9 escolhem a melodia da lista",
    es: "Alt+1 a Alt+9 eligen la melodía de la lista",
    en: "Alt+1 to Alt+9 choose the list's melody",
  },

  // Lessons and the manual.
  listen: { pt: "Ouvir", es: "Escuchar", en: "Listen" },
  listenAnswer: { pt: "Ouvir como deve soar", es: "Escuchar cómo debe sonar", en: "Hear how it should sound" },
  check: { pt: "Conferir", es: "Comprobar", en: "Check" },
  restart: { pt: "Recomeçar", es: "Empezar de nuevo", en: "Start over" },
  showAnswer: { pt: "Ver uma resposta", es: "Ver una respuesta", en: "See an answer" },
  useAnswer: { pt: "Usar esta resposta", es: "Usar esta respuesta", en: "Use this answer" },
  exercise: { pt: "Exercício", es: "Ejercicio", en: "Exercise" },
  example: { pt: "Experimente", es: "Prueba", en: "Try it" },
  right: {
    pt: "Muito bem! Soa exatamente como pedido.",
    es: "¡Muy bien! Suena exactamente como se pedía.",
    en: "Well done! It sounds exactly as asked.",
  },
  almost: { pt: "Quase. Ouça de novo e compare:", es: "Casi. Escucha otra vez y compara:", en: "Almost. Listen again and compare:" },
  andMore: { pt: "e mais", es: "y", en: "and" },
  moreProblems: { pt: "diferenças", es: "diferencias más", en: "more differences" },
  doesNotRead: {
    pt: "O Aratu ainda não consegue ler o seu código; veja a mensagem acima.",
    es: "Aratu todavía no puede leer tu código; mira el mensaje de arriba.",
    en: "Aratu cannot read your code yet; see the message above.",
  },
  previous: { pt: "Anterior", es: "Anterior", en: "Previous" },
  following: { pt: "Próxima", es: "Siguiente", en: "Next" },
  lessons: { pt: "Lições", es: "Lecciones", en: "Lessons" },
  chapters: { pt: "Capítulos", es: "Capítulos", en: "Chapters" },
  doneCount: { pt: "exercícios feitos", es: "ejercicios hechos", en: "exercises done" },
  openInPlay: { pt: "Abrir em Tocar", es: "Abrir en Tocar", en: "Open in Play" },
  pageLink: {
    pt: "Endereço desta página, para ler e compartilhar",
    es: "Dirección de esta página, para leer y compartir",
    en: "This page's address, to read and share",
  },
  readWithout: { pt: "Para ler sem o editor:", es: "Para leer sin el editor:", en: "To read without the editor:" },

  // The pages to read, which work without the editor (scripts/site.mjs).
  language: { pt: "Idioma", es: "Idioma", en: "Language" },
  openInAratu: { pt: "Abrir no Aratu", es: "Abrir en Aratu", en: "Open in Aratu" },
  openHelp: {
    pt: "para ouvir e mudar os exemplos",
    es: "para escuchar y cambiar los ejemplos",
    en: "to hear and change the examples",
  },
  openHelpLesson: {
    pt: "para ouvir os exemplos, mudá-los e fazer os exercícios",
    es: "para escuchar los ejemplos, cambiarlos y hacer los ejercicios",
    en: "to hear the examples, change them and do the exercises",
  },
  openEditor: { pt: "Abrir o editor", es: "Abrir el editor", en: "Open the editor" },
  hearInAratu: { pt: "Ouvir no Aratu", es: "Escuchar en Aratu", en: "Hear it in Aratu" },
  courseName: { pt: "Curso de Aratu", es: "Curso de Aratu", en: "Aratu course" },
  manualName: { pt: "Manual do Aratu", es: "Manual de Aratu", en: "Aratu manual" },
  insideName: { pt: "Aratu por dentro", es: "Aratu por dentro", en: "Inside Aratu" },
  siteIntro: {
    pt: "O Aratu é uma linguagem para escrever música com o teclado do computador e ouvir na hora, do jeito que um músico pensa: em compassos, tempos, notas e cifras. Muda-se o texto enquanto toca, e a música muda junto. Tem um curso, um manual e um editor no navegador, em português, espanhol e inglês.",
    es: "Aratu es un lenguaje para escribir música con el teclado de la computadora y oírla al momento, como piensa un músico: en compases, tiempos, notas y cifrados. Se cambia el texto mientras suena, y la música cambia con él. Tiene un curso, un manual y un editor en el navegador, en portugués, español e inglés.",
    en: "Aratu is a language for writing music on a computer keyboard and hearing it at once, the way a musician thinks: in bars, beats, notes and chords. Change the text while it plays, and the music changes with it. It has a course, a manual and an editor in the browser, in Portuguese, Spanish and English.",
  },
  shareImageAlt: {
    pt: "O caranguejo do Aratu, de fones, tocando num laptop, ao lado do nome Aratu, do lema e de um maracatu escrito em Aratu.",
    es: "El cangrejo de Aratu, con auriculares, tocando en una laptop, junto al nombre Aratu, su lema y un maracatu escrito en Aratu.",
    en: "Aratu's crab, wearing headphones and playing on a laptop, beside the name Aratu, its tagline and a maracatu written in Aratu.",
  },
  courseIntro: {
    pt: "Um curso de Aratu do primeiro compasso a um maracatu: ritmo, bateria, percussão brasileira, melodia, harmonia, forma e música ao vivo. Cada lição tem exemplos para ouvir e mudar, e exercícios que o Aratu confere pelo som.",
    es: "Un curso de Aratu del primer compás a un maracatu: ritmo, batería, percusión brasileña, melodía, armonía, forma y música en vivo. Cada lección tiene ejemplos para escuchar y cambiar, y ejercicios que Aratu comprueba por el sonido.",
    en: "A course in Aratu from the first bar to a maracatu: rhythm, drums, Brazilian percussion, melody, harmony, form and live music. Each lesson has examples to hear and change, and exercises Aratu checks by their sound.",
  },

  // Example pieces.
  "first-piece": { pt: "Primeira peça: deep house", es: "Primera pieza: deep house", en: "First piece: deep house" },
  chance: { pt: "Acaso: techno generativo", es: "Azar: techno generativo", en: "Chance: generative techno" },
  melody: { pt: "Melodia com dinâmica: chillout", es: "Melodía con matices: chillout", en: "Melody with dynamics: chillout" },
  patterns: { pt: "Padrões ao vivo: techno", es: "Patrones en vivo: techno", en: "Live patterns: techno" },
  form: { pt: "Seções e forma: piano house", es: "Secciones y forma: piano house", en: "Sections and form: piano house" },
  waltz: { pt: "Valsa eletrônica com anacruse", es: "Vals electrónico con anacrusa", en: "Electronic waltz with a pickup" },
  sound: { pt: "Sons e knobs: acid house", es: "Sonidos y perillas: acid house", en: "Sounds and knobs: acid house" },
  motifs: { pt: "Motivos: synthwave", es: "Motivos: synthwave", en: "Motifs: synthwave" },
  "twelve-tone": { pt: "Série dodecafônica: IDM", es: "Serie dodecafónica: IDM", en: "Twelve-tone row: IDM" },
  setlist: { pt: "Listas no palco: electro house", es: "Listas en el escenario: electro house", en: "Lists on stage: electro house" },
  sweeps: { pt: "Efeitos que se movem: progressive house", es: "Efectos que se mueven: progressive house", en: "Effects that move: progressive house" },
  trance: { pt: "Trance em dó menor", es: "Trance en do menor", en: "Trance in C minor" },
  manguebeat: { pt: "Manguebeat eletrônico", es: "Manguebeat electrónico", en: "Electronic manguebeat" },
  "drum-and-bass": { pt: "Drum and bass: notas fantasma", es: "Drum and bass: notas fantasma", en: "Drum and bass: ghost notes" },
  "g-funk": { pt: "G-funk da Costa Oeste", es: "G-funk de la Costa Oeste", en: "West Coast G-funk" },
  chamber: { pt: "Música de câmara: cordas, sopros e coro", es: "Música de cámara: cuerdas, vientos y coro", en: "Chamber music: strings, winds and choir" },
} as const;

export type Word = keyof typeof words;

export function tr(word: Word, lang: Lang): string {
  return words[word][lang];
}

export function isWord(w: string): w is Word {
  return w in words;
}
